import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, Code2, Smartphone, Zap, Shield, Server, Users, ArrowRight, Menu, X } from 'lucide-react';
import ContactForm from '../components/ContactForm';
import CookieBanner from '../components/CookieBanner';
import { mockData } from '../mock';

const Home = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [visibleSections, setVisibleSections] = useState([]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      // Animate sections on scroll
      const sections = document.querySelectorAll('.animate-on-scroll');
      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.8) {
          section.classList.add('visible');
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-3 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => scrollToSection('hero')}>
              <img 
                src="/images/logo.png" 
                alt="AppPilot Logo" 
                className="h-14 w-auto"
              />
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-orange-500 transition-colors font-medium">
                Leistungen
              </button>
              <button onClick={() => scrollToSection('features')} className="text-gray-700 hover:text-orange-500 transition-colors font-medium">
                Vorteile
              </button>
              <button onClick={() => scrollToSection('portfolio')} className="text-gray-700 hover:text-orange-500 transition-colors font-medium">
                Referenzen
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-orange-500 transition-colors font-medium">
                Kontakt
              </button>
              <Button 
                onClick={() => scrollToSection('contact')} 
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
              >
                Jetzt starten
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-gray-700 hover:text-orange-500 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-3 border-t border-gray-200 animate-in slide-in-from-top">
              <button onClick={() => scrollToSection('services')} className="block w-full text-left px-4 py-2 text-gray-700 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-colors">
                Leistungen
              </button>
              <button onClick={() => scrollToSection('features')} className="block w-full text-left px-4 py-2 text-gray-700 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-colors">
                Vorteile
              </button>
              <button onClick={() => scrollToSection('portfolio')} className="block w-full text-left px-4 py-2 text-gray-700 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-colors">
                Referenzen
              </button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left px-4 py-2 text-gray-700 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-colors">
                Kontakt
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section id="hero" className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-white to-red-50 opacity-70"></div>
        <div className="container mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-200 border-orange-200 inline-flex items-center gap-2">
                <Zap size={16} /> Ihre digitale Zukunft beginnt hier
              </Badge>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                WebApps, die Ihr
                <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent"> Geschäft </span>
                voranbringen
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Maßgeschneiderte Webanwendungen für kleine und mittelständische Unternehmen. 
                Kostengünstig, problemorientiert und mit Fokus auf Datenschutz.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={() => scrollToSection('contact')}
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all text-lg"
                >
                  Kostenloses Beratungsgespräch
                  <ArrowRight className="ml-2" size={20} />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => scrollToSection('services')}
                  className="border-2 border-orange-500 text-orange-500 hover:bg-orange-50 transition-all text-lg"
                >
                  Mehr erfahren
                </Button>
              </div>
              <div className="flex items-center gap-8 pt-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="text-green-500" size={20} />
                  <span className="text-sm text-gray-600">100% Datenschutz</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="text-green-500" size={20} />
                  <span className="text-sm text-gray-600">Server in EU/DE</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="text-green-500" size={20} />
                  <span className="text-sm text-gray-600">Kostengünstig</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                <img 
                  src="/images/hero-web-development.jpg"
                  alt="Modern Web Development"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-orange-900/50 to-transparent"></div>
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-6 transform hover:scale-110 transition-transform">
                <div className="text-3xl font-bold text-orange-500">100%</div>
                <div className="text-sm text-gray-600">Zufriedenheit</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16 animate-on-scroll" style={{opacity: 0, transform: 'translateY(30px)', transition: 'all 0.8s ease-out'}}>
            <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-200 border-orange-200 mb-4">
              Unsere Leistungen
            </Badge>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Digitale Lösungen für Ihr Business
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Wir entwickeln maßgeschneiderte Webanwendungen, die Ihre Geschäftsprozesse optimieren
              und Ihren Arbeitsalltag erleichtern.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockData.services.map((service, index) => (
              <Card 
                key={index} 
                className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 hover:border-orange-200 animate-on-scroll"
                style={{opacity: 0, transform: 'translateY(30px)', transition: `all 0.8s ease-out ${index * 0.1}s`}}
              >
                <CardHeader>
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-100 to-red-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    {service.icon === 'Code2' && <Code2 className="text-orange-500" size={28} />}
                    {service.icon === 'Smartphone' && <Smartphone className="text-orange-500" size={28} />}
                    {service.icon === 'Zap' && <Zap className="text-orange-500" size={28} />}
                    {service.icon === 'Users' && <Users className="text-orange-500" size={28} />}
                    {service.icon === 'Shield' && <Shield className="text-orange-500" size={28} />}
                    {service.icon === 'Server' && <Server className="text-orange-500" size={28} />}
                  </div>
                  <CardTitle className="text-2xl group-hover:text-orange-500 transition-colors">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-base leading-relaxed">
                    {service.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto">
          <div className="text-center mb-16 animate-on-scroll" style={{opacity: 0, transform: 'translateY(30px)', transition: 'all 0.8s ease-out'}}>
            <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-200 border-orange-200 mb-4">
              Warum AppPilot?
            </Badge>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Ihre Vorteile auf einen Blick
            </h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-6 animate-on-scroll" style={{opacity: 0, transform: 'translateY(30px)', transition: 'all 0.8s ease-out'}}>
              {mockData.features.map((feature, index) => (
                <div key={index} className="flex items-start gap-4 group hover:bg-white p-6 rounded-xl transition-all hover:shadow-lg">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-orange-100 to-red-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    {feature.icon === 'Shield' && <Shield className="text-orange-500" size={24} />}
                    {feature.icon === 'Server' && <Server className="text-orange-500" size={24} />}
                    {feature.icon === 'Zap' && <Zap className="text-orange-500" size={24} />}
                    {feature.icon === 'CheckCircle2' && <CheckCircle2 className="text-orange-500" size={24} />}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-orange-500 transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16 animate-on-scroll" style={{opacity: 0, transform: 'translateY(30px)', transition: 'all 0.8s ease-out'}}>
            <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-200 border-orange-200 mb-4">
              Referenzen
            </Badge>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Unsere Erfolgsgeschichten
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Entdecken Sie, wie wir anderen Unternehmen geholfen haben, ihre digitalen Ziele zu erreichen.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {mockData.portfolio.map((project, index) => (
              <Card 
                key={index}
                className="group hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden animate-on-scroll"
                style={{opacity: 0, transform: 'translateY(30px)', transition: `all 0.8s ease-out ${index * 0.2}s`}}
              >
                <div className="relative overflow-hidden h-64">
                  <img 
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <CardHeader>
                  <div className="flex items-center gap-2 mb-2">
                    {project.tags.map((tag, idx) => (
                      <Badge key={idx} variant="secondary" className="bg-orange-100 text-orange-700">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  <CardTitle className="text-2xl group-hover:text-orange-500 transition-colors">
                    {project.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-base leading-relaxed mb-4">
                    {project.description}
                  </CardDescription>
                  {project.link && (
                    <a 
                      href={project.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-orange-500 hover:text-orange-600 font-medium transition-colors"
                    >
                      Projekt ansehen
                      <ArrowRight size={16} />
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12 animate-on-scroll" style={{opacity: 0, transform: 'translateY(30px)', transition: 'all 0.8s ease-out'}}>
            <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-200 border-orange-200 mb-4">
              Kontakt
            </Badge>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Lassen Sie uns Ihr Projekt besprechen
            </h2>
            <p className="text-xl text-gray-600">
              Kontaktieren Sie uns für ein unverbindliches Beratungsgespräch.
            </p>
          </div>
          <div className="animate-on-scroll" style={{opacity: 0, transform: 'translateY(30px)', transition: 'all 0.8s ease-out 0.2s'}}>
            <ContactForm />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <img 
                src="/images/logo-white.png" 
                alt="AppPilot Logo" 
                className="h-12 w-auto mb-4"
              />
              <p className="text-gray-400 leading-relaxed mb-4">
                Ihr Partner für maßgeschneiderte Webanwendungen. 
                Wir entwickeln digitale Lösungen, die Ihr Geschäft voranbringen.
              </p>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Server size={16} />
                <span>Server in der EU, auf Wunsch ausschließlich in Deutschland</span>
              </div>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Navigation</h3>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-gray-400 hover:text-orange-400 transition-colors">
                    Leistungen
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('features')} className="text-gray-400 hover:text-orange-400 transition-colors">
                    Vorteile
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('portfolio')} className="text-gray-400 hover:text-orange-400 transition-colors">
                    Referenzen
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-orange-400 transition-colors">
                    Kontakt
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Rechtliches</h3>
              <ul className="space-y-2">
                <li>
                  <a href="/impressum" className="text-gray-400 hover:text-orange-400 transition-colors">
                    Impressum
                  </a>
                </li>
                <li>
                  <a href="/datenschutz" className="text-gray-400 hover:text-orange-400 transition-colors">
                    Datenschutzerklärung
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; {new Date().getFullYear()} AppPilot. Alle Rechte vorbehalten.</p>
          </div>
        </div>
      </footer>

      {/* Cookie Banner */}
      <CookieBanner />

      <style jsx>{`
        .animate-on-scroll {
          opacity: 0;
          transform: translateY(30px);
        }
        .animate-on-scroll.visible {
          opacity: 1 !important;
          transform: translateY(0) translateX(0) !important;
        }
      `}</style>
    </div>
  );
};

export default Home;